import React, { useMemo, useEffect, useRef } from 'react';
import { history, useIntl, getLocale } from 'umi';
import moment from 'moment';
import { EChartsType } from 'echarts';
import { Table, Col, Row, TableColumn } from 'tea-component';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import TableProgress from '@/pages/intelligence/components/TableComponents/components/TableProgress';
import SortBy from '@/pages/intelligence/components/TableComponents/components/SortBy';
import SvgIcon from '@/pages/home/components/SvgIcon';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { formatPercentNum } from '../../../../utils';
import { formatNum } from '@/pages/intelligence/utils/commonUtils';
import ChartBubble, { TOP_CHARTS_TYPES } from '@/pages/intelligence/components/ChartBubble';
import {
  DataSourceBubbleInfo,
  DATASOURCE_NAME,
} from '@/pages/intelligence/components/DataSourceComponents';
import { SUB_MODULE_CONFIG } from '../../config';
import { IEntityType, IFixedColTableProp, ITableMetricTips } from '../../../../typings';
import { METRICS_TEXT_MAP } from '../../../../components/DynamicColTable/config';

import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

export default function FixedColTable(props: IFixedColTableProp) {
  const {
    title,
    tableData,
    lineData,
    date,
    dataSource,
    isTableLoading,
    isLineLoading,
    entityType,
    action,
    onLineChartItemClick,
    onGameItemClick,
    onTextClick,
  } = props;

  const {
    title: sourceName,
    value: sourceValue,
    metric: metricName,
    titleTips,
    metricExplanation,
  } = dataSource;
  const handleTitleClick = (clickItem: any) => {
    onGameItemClick('', clickItem);
  };

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const tagetMetricExplanation = useMemo(() => {
    const metricTextList = metricExplanation || METRICS_TEXT_MAP[`${sourceValue}_${metricName}`];
    let str = '';
    metricTextList?.forEach((textKey: string) => {
      str += formatMessage({ id: textKey });
    });
    return str;
  }, [locale, sourceValue, metricName, metricExplanation]);

  const columns: TableColumn[] = [
    {
      key: 'Top Game',
      header: formatMessage({ id: 'dbi_market_top_game' }),
      align: 'left',
      render: (cvm: any) => {
        const { rank, text = [], linkUrl } = cvm || {};
        return (
          <FirstRow
            id={rank}
            hasBadge={true}
            rihgtClickToJumpHref={linkUrl || ''}
            cvm={{
              ...cvm,
              text: text?.map((str: string, index: number) => {
                if (index === text.length - 1) return <span>{str}</span>;
                return (
                  <>
                    <span>{str}</span>
                    <span>|</span>
                  </>
                );
              }),
            }}
            onClick={() => {
              handleTitleClick(cvm);
            }}
          />
        );
      },
    },
    {
      key: sourceName,
      align: 'right',
      width: entityType === 'mobile' ? 150 : 200,
      header: (
        <SortBy
          defaultKey=""
          defaultValue=""
          title={formatMessage({ id: sourceName })}
          svgIcon={getDataSourceIcon(sourceValue)}
          isNoDropdown={true}
          svgTip={
            <DataSourceBubbleInfo
              dataOfDom={sourceName}
              metricKey={metricName}
              entityType={entityType}
              comesFormDom={
                <>
                  <img src={getDataSourceIcon(sourceValue)} />
                  {DATASOURCE_NAME[sourceValue]}
                </>
              }
              dataSource={sourceValue}
            />
          }
          content={<>{tagetMetricExplanation}</>}
        />
      ),
      render: (cvm: any) => {
        return <span className="tabdetails-table-text">{cvm.dau}</span>;
      },
    },
    {
      key: '% of Market',
      width: 135,
      header: (
        <SortBy
          defaultKey=""
          defaultValue=""
          title={formatMessage({ id: 'dbi_genre_profile_metric_percent_market' })}
          isNoDropdown={true}
        />
      ),
      align: 'right',
      render: (cvm: any) => {
        return <TableProgress cvm={cvm.percent} />;
      },
    },
  ];

  const lineConfig = useMemo(() => {
    const {
      xAxis,
      xAxisOri,
      series: { line, bar },
    } = SUB_MODULE_CONFIG.marketCRTenByRevenue.getXAxisAndSeriesData(lineData, entityType);
    const getTitle = () => {
      return {
        show: false,
        text: 'Market CR10 By Revenue',
        left: '0',
        textStyle: {
          color: '#000',
          fontWeight: 'bold',
          fontSize: 14,
        },
      };
    };
    const getTooltip = () => {
      return {
        trigger: 'axis',
        padding: 0,
        borderWidth: 0,
        formatter: (chartItem: any[]) => {
          return ChartBubble({
            topChartsType: TOP_CHARTS_TYPES.GENRE_PROFILE_INSIGHTS_LINE,
            chartType: 'line',
            secondTitleLeft: chartItem[0]?.axisValueLabel,
            secondTitleRight: formatMessage({ id: 'dbi_revenue_$' }),
            data: chartItem,
          });
        },
      };
    };
    const getGrid = () => {
      return {
        top: '8%',
        left: '5%',
        bottom: '13%',
        right: '3%',
      };
    };
    const getLegend = () => {
      return {
        show: true,
        bottom: 0,
        lineStyle: {
          opacity: 0,
        },
        textStyle: {
          color: '#838697',
        },
        icon: 'circle',
        itemWidth: 8,
      };
    };
    const getXAxis = () => {
      return {
        type: 'category',
        data: xAxis,
        originalData: xAxisOri,
        axisLabel: {
          color: '#838697',
        },
        axisTick: {
          alignWithLabel: true,
          lineStyle: {
            color: '#eeeeee',
          },
        },
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: '#0076FF',
            opacity: '0.05',
          },
        },
      };
    };
    const getYAxis = () => {
      return [
        {
          type: 'value',
          position: 'left',
          axisLine: {
            show: false,
          },
          splitNumber: 5,
          axisLabel: {
            color: '#838697',
            formatter(value: string) {
              return formatNum(value);
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#eeeeee',
            },
          },
        },
        {
          type: 'value',
          position: 'right',
          axisLine: {
            show: false,
          },
          splitNumber: 5,
          axisLabel: {
            color: '#838697',
            formatter(value: string) {
              return formatPercentNum(value);
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
            lineStyle: {
              color: '#eeeeee',
            },
          },
        },
      ];
    };
    const getSeries = () => {
      return [
        {
          name: 'CR10',
          type: 'bar',
          data: bar,
          color: '#5086f3',
          emphasis: {
            itemStyle: {
              color: '#5086f3',
            },
          },
        },
        {
          name: 'CR10 in %',
          type: 'line',
          yAxisIndex: 1,
          color: '#69D19C',
          symbol: 'none',
          data: line,
        },
      ];
    };
    return {
      title: getTitle(),
      tooltip: getTooltip(),
      grid: getGrid(),
      legend: getLegend(),
      xAxis: getXAxis(),
      yAxis: getYAxis(),
      series: getSeries(),
    };
  }, [lineData, locale]);

  const getActionDom = (_action: any) => {
    if (!_action) return null;
    const { type, text, linkTo } = _action;
    if (type === 'text') {
      return (
        <span className="insights-right-text" onClick={() => history.replace(linkTo)}>
          {text}
        </span>
      );
    }
  };

  // const lineChartRef = useRef<{ instance(): EChartsType }>(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);

  const bindEvent = (ins: any) => {
    ins?.getZr()?.off('click');
    ins?.getZr()?.on('click', (params: any) => {
      // 获取 点击的 触发点像素坐标
      const pointInPixel = [params.offsetX, params.offsetY];
      // 判断给定的点是否在指定的坐标系或者系列上
      if (ins.containPixel('grid', pointInPixel)) {
        // 获取到点击的 x轴 下标  转换为逻辑坐标
        const xIndex = ins.convertFromPixel({ seriesIndex: 0 }, pointInPixel)[0];
        const clickTime = lineConfig.xAxis?.originalData?.[xIndex];
        if (clickTime) {
          onLineChartItemClick(moment(clickTime).format('YYYY-MM-DD'));
        }
      }
    });
    ins?.getZr()?.on('mousemove', (params: any) => {
      const pointInPixel = [params.offsetX, params.offsetY];
      if (ins.containPixel('grid', pointInPixel)) {
        // 将此区域的 鼠标样式变为 小手
        ins.getZr().setCursorStyle('pointer');
      }
    });
  };

  // useEffect(() => {
  //   if (isLineLoading) return;
  //   const lineChartIns = lineChartRef.current?.instance();
  //   if (!lineChartIns) return;
  //   bindEvent(lineChartIns);
  // }, [lineChartRef.current, isLineLoading]);

  useEffect(() => {
    if (isLineLoading) return;
    const lineChartIns = chartsInstance.current?.getLineInstance();
    if (!lineChartIns) return;
    bindEvent(lineChartIns);
  }, [isLineLoading]);

  return (
    <CardSlot
      title={title}
      iconList={[]}
      rightDom={getActionDom(action)}
      className="dbi-insights-page-card"
    >
      <div className="tabdetails-echarts">
        <ChartsIndex
          ref={chartsInstance}
          chartLoading={isLineLoading}
          lineOption={lineConfig}
          chartType={'line'}
          isNodata={!lineData?.length}
          // lineChartRef={lineChartRef}
        />
      </div>

      <div className="insights-gamehead">
        <div
          className="insights-gamehead-title canclick"
          onClick={() => {
            if (onTextClick instanceof Function) {
              onTextClick();
            }
          }}
        >
          {formatMessage({ id: 'dbi_market_top_game' })}
          <SvgIcon className="insights-gamehead-arrow" type="arrow-right" />
        </div>
        <div className="insights-gamehead-text">{date}</div>
      </div>

      <Abnormal
        isLoading={isTableLoading}
        isNoData={!isTableLoading && !tableData?.length}
        component={
          <Row className="insights-rowtop">
            <Col span={12}>
              <Table
                key={'top-five-table'}
                className="tabdetails-table"
                records={tableData.slice(0, 5)}
                columns={columns}
                addons={[]}
              />
            </Col>
            <Col span={12}>
              <Table
                key={'top-five-ten-table'}
                className="tabdetails-table"
                records={tableData.slice(5)}
                columns={columns}
                addons={[]}
              />
            </Col>
          </Row>
        }
      />
    </CardSlot>
  );
}
